'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useModalStore } from '#pplx/frontend/aether/modals/store/useModalStore.js';
import { useGdprConsent } from '#pplx/frontend/analytics/browser/useGdprConsent.js';
import { isFirefoxDefaultSearch } from '#pplx/frontend/apps/ask/browser/firefoxUtils.js';
import { useHardLoginGateVariation } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useHardLoginGateVariation.js';
import { useAllowedFloatingUpsellPath } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useAllowedFloatingUpsellPath.js';
import { isComputerRoute } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/constants.js';
import { useGetVisitorInformation } from '#pplx/frontend/apps/ask/queries/visitor-queries/useGetVisitorInformation.js';
import { useShouldShowFloatingSignup } from '#pplx/frontend/apps/ask/signup/useShowFloatingSignup.js';
import { SideCardUpsellSlot } from '#pplx/frontend/apps/ask/upsell-slots/side-card/SideCardUpsellSlot.js';
import { OmitUpsellIf } from '#pplx/frontend/apps/ask/upsells/components/OmitUpsellIf.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { ErrorBoundary } from '#pplx/frontend/platform-components/ErrorBoundary.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useRef } from 'react';
const BackendFlagOverrideWidget = Dynamic(() => import('#pplx/frontend/apps/ask/restricted-feature-debug-backend-flag-override/index.js'), { restricted: true });
const FloatingSignUp = Dynamic(() => import('#pplx/frontend/apps/ask/signup-ui/FloatingSignup.js'));
const CookieConsentPopup = Dynamic(() => import('#pplx/frontend/analytics/components/CookieConsentPopup.js'));
const FloatingComponentsInner = () => {
    const reason = 'floating-components';
    const { status } = useSession();
    const { showCookieConsentPopup, hasTrackingPolicyResolved, closeCookieConsentPopup, consentMode, } = useGdprConsent();
    const floatingSignupContainerRef = useRef(null);
    const pathname = usePathname();
    const [shouldShowFloatingSignup, setShowFloatingSignup] = useShouldShowFloatingSignup();
    const isAllowedPath = useAllowedFloatingUpsellPath();
    const { isMobileStyle } = useIsMobile();
    const { device: { isWindowsApp }, } = useWebPlatform();
    // Hard login gate check for thread pages
    const { data: visitorInfo } = useGetVisitorInformation({ reason });
    const visitorDailyQueryCount = visitorInfo?.visitor_queries_past_day;
    const { variation: hardLoginGateVariation } = useHardLoginGateVariation({
        visitorDailyQueryCount,
    });
    const isOnThreadPage = pathname?.startsWith('/search/');
    const isOnComputerPath = isComputerRoute(pathname);
    const shouldHideFloatingSignupForHardGate = isOnThreadPage && hardLoginGateVariation !== 'control';
    const hasModalsOpen = useModalStore((store) => store.modals.size > 0);
    if (!hasTrackingPolicyResolved) {
        return null;
    }
    if (showCookieConsentPopup) {
        return (_jsx(CookieConsentPopup, { closeCookieConsentPopup: closeCookieConsentPopup, consentMode: consentMode, surface: "ask" }));
    }
    const showFloatingSignup = status === 'unauthenticated' &&
        !import.meta.env.VITE_IS_SIDECAR_BUILD &&
        shouldShowFloatingSignup &&
        !hasModalsOpen &&
        !shouldHideFloatingSignupForHardGate;
    const isDesktopAppBuild = !!import.meta.env.VITE_IS_SIDECAR_BUILD ||
        !!import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD ||
        !!import.meta.env.VITE_IS_COMPUTER_APP_BUILD ||
        !!import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD ||
        isWindowsApp;
    const sideCardUpsellSlotCallsite = {
        enabled: isAllowedPath,
        pathname,
        routeKind: 'home',
        surface: {
            hasCompetingModal: hasModalsOpen,
            hasCookieConsentResolved: hasTrackingPolicyResolved,
            isDesktopAppBuild,
            hasPrimarySurfaceContent: showFloatingSignup,
            isAllowedFloatingPath: isAllowedPath,
            isComputerRoute: isOnComputerPath,
            isFirefoxDefaultSearch: isFirefoxDefaultSearch(),
            isMobileLayout: isMobileStyle,
        },
    };
    // These are floating components that are rendered in the base layout so that
    // they do not flicker when the route changes and AppLayoutV2 is unmounted
    // and remounted.
    return (_jsxs(_Fragment, { children: [showFloatingSignup && (_jsx("div", { ref: floatingSignupContainerRef, children: _jsx(OmitUpsellIf, { isWindowsApp: true, isGovernment: true, children: _jsx(FloatingSignUp, { portalTarget: floatingSignupContainerRef.current ?? undefined, onClose: () => setShowFloatingSignup(false) }, "floating-signup") }) })), _jsx(SideCardUpsellSlot, { callsite: sideCardUpsellSlotCallsite }), _jsx(BackendFlagOverrideWidget, {})] }));
};
export const FloatingComponents = () => (_jsx(ErrorBoundary, { fallback: null, code: "FLOATING_COMPONENTS_ERROR", children: _jsx(FloatingComponentsInner, {}) }));
//# sourceMappingURL=FloatingComponents.js.map